import {Animation, Detail, Tooltip} from '@forecast-it/design-system';
import React, {useLayoutEffect, useRef} from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {dispatch, EVENT_ID} from '../../../../containers/event_manager';
import {DISABLED_REASON_MESSAGE_ID, type ControlState} from './controlState';
import {anchorElementId, novaInstanceKey, controlElementId, useIsPickerOpen} from './NovaAgentPicker';
import {formatCreditsReason} from './novaCreditsCopy';
import {
	HeaderButton,
	HeaderSquareButton,
	LoaderSlot,
	RowButton,
	RowSquareButton,
	runningButtonStyle,
	SplitRow,
} from './novaDsControls';
import {RUN_CAP} from './novaInvocationStore';

export interface InvocationControlProps {
	taskId: string;
	// See NovaAgentPickerProps.instanceKey (NovaAgentPicker.tsx) — must match whatever the
	// sibling agentPicker below was given, or the caret's id and the picker's toggle/lookup key
	// disagree and the picker can't find its own caret.
	instanceKey?: string;
	// Where this control is mounted. 'row' is the quiet control a task list shows among other
	// chips; 'header' is the filled primary the task modal gives it, where starting a run is the
	// one thing the header offers. Only the emphasis and size differ - every state, label and
	// refusal below is the same control either way, because a person meets both and they must not
	// disagree.
	variant?: 'row' | 'header';
	state: ControlState;
	// The agent behind the current or last run, when naming it tells the reader something. Null for
	// every default-agent state, which carries no tooltip at all - see the tooltip note below.
	specialistTitle?: string | null;
	// The day a spent AI allowance resets, for the out_of_credits reason.
	creditsResetsOn?: string | null;
	onAction: () => void;
	onOpenOutput: () => void;
	onStop: () => void;
	agentPicker?: React.ReactNode; // rendered inline below the control when the picker is mounted
}

let disabledReasonIdCounter = 0;

function useDisabledReasonId(): string {
	const idRef = useRef<string>();
	if (!idRef.current) {
		disabledReasonIdCounter += 1;
		idRef.current = `nova-disabled-reason-${disabledReasonIdCounter}`;
	}
	return idRef.current;
}

// A disabled button fires no pointer events, so a tooltip bound to it never opens. The reason has
// to hang off something that does: this wrapper takes the hover, and carries the refused cursor so
// the control reads as refused before the reason arrives.
const TooltipAnchor = styled.span<{$refused: boolean}>`
	display: inline-flex;
	align-items: center;

	${({$refused}) =>
		$refused &&
		`
		cursor: not-allowed;

		> * {
			pointer-events: none;
		}
	`}
`;

// Task rows are dense, so the tooltip holds one line that never wraps - a tooltip that reflows
// over a row is harder to read than the label it is explaining. The modal header has the room,
// and some of its reasons are a full sentence longer than the tooltip is wide: held to one line
// there, the text ran out past the tooltip's edge, so the header's wraps inside it instead.
const TooltipLine = styled.span<{$wrap: boolean}>`
	display: block;
	white-space: ${({$wrap}) => ($wrap ? 'normal' : 'nowrap')};
`;

// This region exists only so a screen reader announces the working transition (see the focus
// effect below) — it must never be visible. Standard "visually hidden" clip, not display:none,
// so it stays in the accessibility tree while taking no visual space.
const StatusRegion = styled.span`
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
`;

export const NovaInvocationControl = (props: InvocationControlProps): JSX.Element | null => {
	const {state} = props;
	const intl = useIntl();
	const {formatMessage} = intl;
	const isHeader = props.variant === 'header';
	const disabledReasonId = useDisabledReasonId();
	const stopButtonRef = useRef<HTMLButtonElement>(null);

	const Main = isHeader ? HeaderButton : RowButton;
	const Square = isHeader ? HeaderSquareButton : RowSquareButton;
	// On a row the control sits among other chips and stays quiet, so only Ready - the state asking
	// to be looked at - is filled. In the modal header the control is the one thing that starts a
	// run, and the design gives it the filled primary in every state.
	const emphasis = (isReady: boolean): 'high' | 'medium' => (isHeader || isReady ? 'high' : 'medium');

	// The Working control renders unpressable — a browser can't keep focus on a button the instant
	// it becomes disabled, so a keyboard user who just pressed Action would otherwise lose focus to
	// the document body the moment the row flips to Working. Moving it onto the new stop button
	// keeps a keyboard user's focus somewhere useful. previousKindRef excludes the initial mount (a
	// row that starts out already Working, e.g. on page load) — only an actual action→working
	// transition should steal focus.
	const previousKindRef = useRef<ControlState['kind'] | null>(null);
	// A favourited task on My Work mounts two instances of this control for the same taskId
	// (Draggable_card's card and Task_row's row — see each one's own instanceKey comment). Both
	// subscribe to the same store entry and both observe the same action→working transition, so
	// without this guard, clicking Action on one instance moves focus onto whichever instance's
	// effect happens to run last (React runs sibling effects in mount order), not necessarily the
	// one actually clicked. Set synchronously in the click handler below, read and cleared here —
	// only the instance whose own button raised this transition ever moves focus.
	const actionClickedHereRef = useRef(false);
	useLayoutEffect(() => {
		if (
			previousKindRef.current !== null &&
			previousKindRef.current !== 'working' &&
			state.kind === 'working' &&
			actionClickedHereRef.current
		) {
			stopButtonRef.current?.focus();
		}
		actionClickedHereRef.current = false;
		previousKindRef.current = state.kind;
	}, [state.kind]);

	const pickerOpen = useIsPickerOpen(props.taskId, props.instanceKey);

	// Removed from the page rather than hidden with CSS, so a screen reader and the tab order skip
	// it as completely as the eye does. There is nothing else to draw in its place: the absence of
	// the control is the state, and a banner explaining a permission would be the advertisement
	// that withholding it exists to avoid.
	if (state.kind === 'hidden') return null;

	let control: React.ReactNode;
	let statusText = '';
	let disabledReasonSpan: React.ReactNode = null;
	let tooltip = '';
	let refused = false;

	switch (state.kind) {
		case 'disabled': {
			// RUN_CAP is passed for every reason, not just run_cap_reached - react-intl ignores an
			// interpolation value a message doesn't use, and this keeps the run-cap message honest if
			// RUN_CAP ever changes, instead of a hardcoded "3" silently going stale in five locales.
			const reasonText =
				state.reason === 'out_of_credits'
					? formatCreditsReason(intl, props.creditsResetsOn)
					: formatMessage({id: DISABLED_REASON_MESSAGE_ID[state.reason]}, {count: RUN_CAP});
			tooltip = reasonText;
			refused = true;
			control = (
				<SplitRow>
					{/* No caret beside it. The picker it would open chooses an agent for a run that
					    cannot start, so it would open onto a choice with nothing to apply it to -
					    and on the out-of-credits and no-agent reasons there is nothing to choose
					    from either. One refused control, not two.

					    Continue when it is held back until the error the conversation ended in is
					    dealt with: what is refused is going on with this conversation, not starting
					    a new one. */}
					<Main
						emphasis={emphasis(false)}
						leftIcon="sparkle"
						borderRadiusSide={undefined}
						disabled
						useButtonProps
						noMargin
						title={reasonText}
						aria-describedby={disabledReasonId}
						data-cy="nova-invocation-control"
					>
						{formatMessage({
							id:
								state.reason === 'last_run_failed'
									? 'nova_invocation.control.continue'
									: 'nova_invocation.control.invoke',
						})}
					</Main>
				</SplitRow>
			);
			// Visually hidden — aria-describedby (on the button above) reads this for a screen
			// reader regardless of visibility. The tooltip already covers sighted users.
			disabledReasonSpan = <StatusRegion id={disabledReasonId}>{reasonText}</StatusRegion>;
			break;
		}
		case 'working':
			statusText = formatMessage({id: 'nova_invocation.control.status_working'});
			control = (
				<SplitRow>
					{/* Not disabled: there is nothing to press while a run is going, but the control is
					    not refused either, so it keeps its live emphasis and only drops the pointer.
					    aria-disabled says the same thing to assistive tech without the greying-out or
					    the focus loss that `disabled` would bring - see the focus effect above. */}
					<Main
						emphasis={emphasis(false)}
						borderRadiusSide="left"
						useButtonProps
						noMargin
						style={runningButtonStyle}
						aria-disabled
						title={
							props.specialistTitle
								? formatMessage(
										{id: 'nova_invocation.control.tooltip_working_specialist'},
										{agent: props.specialistTitle}
								  )
								: undefined
						}
						aria-label={formatMessage({id: 'nova_invocation.control.status_working'})}
						data-cy="nova-invocation-control"
					>
						{/* The design system's own loader, in place of the spinner this control used to
						    draw for itself. The white cut on the filled header, the tinted one on a row. */}
						<LoaderSlot $size={isHeader ? 16 : 14} aria-hidden="true">
							<Animation animation={isHeader ? 'loaderWhite' : 'loader'} />
						</LoaderSlot>
						{formatMessage({id: 'nova_invocation.control.working'})}
					</Main>
					<Square
						ref={stopButtonRef}
						emphasis={emphasis(false)}
						leftIcon="stop"
						borderRadiusSide="right"
						iconOnly
						useButtonProps
						noMargin
						onPress={props.onStop}
						title={formatMessage({id: 'nova_invocation.control.stop'})}
						aria-label={formatMessage({id: 'nova_invocation.control.stop'})}
						data-cy="nova-invocation-stop"
					/>
				</SplitRow>
			);
			break;
		case 'view_only':
			// One control, and the only one this person gets: an answer exists on the task and they
			// may read it. Medium emphasis in both variants, never the filled Ready treatment and
			// never the unread tint - both of those exist to pull somebody towards output that is
			// theirs to act on, and this output is not. No caret and no stop, because there is no
			// run here to steer or end.
			tooltip = formatMessage({id: 'nova_invocation.control.tooltip_view'});
			control = (
				<SplitRow>
					<Main
						emphasis="medium"
						leftIcon="sparkle"
						borderRadiusSide={undefined}
						useButtonProps
						noMargin
						onPress={props.onOpenOutput}
						data-cy="nova-invocation-control"
					>
						{formatMessage({id: 'nova_invocation.control.view'})}
					</Main>
				</SplitRow>
			);
			break;
		case 'ready_or_continue':
			// Neither Ready nor Continue carries an agent caret: both only open the task, so there is
			// no run here to configure. A conversation's agent is changed from the conversation
			// itself, where the warning about discarding it lives. The row around it stays, so that
			// losing the second half does not take the first half's DOM node with it - see the note
			// on the row itself.
			control = (
				<SplitRow>
					<Main
						emphasis={emphasis(!state.read)}
						leftIcon="sparkle"
						borderRadiusSide={undefined}
						useButtonProps
						noMargin
						title={
							props.specialistTitle
								? formatMessage(
										{id: 'nova_invocation.control.tooltip_continue_specialist'},
										{agent: props.specialistTitle}
								  )
								: undefined
						}
						onPress={props.onOpenOutput}
						data-cy="nova-invocation-control"
					>
						{formatMessage({
							id: state.read ? 'nova_invocation.control.continue' : 'nova_invocation.control.ready',
						})}
					</Main>
				</SplitRow>
			);
			break;
		case 'action':
			control = (
				<SplitRow>
					<Main
						emphasis={emphasis(false)}
						leftIcon="sparkle"
						borderRadiusSide="left"
						useButtonProps
						noMargin
						onPress={() => {
							actionClickedHereRef.current = true;
							props.onAction();
						}}
						data-cy="nova-invocation-control"
					>
						{formatMessage({id: 'nova_invocation.control.invoke'})}
					</Main>
					<Square
						id={anchorElementId(props.taskId, props.instanceKey)}
						emphasis={emphasis(false)}
						leftIcon={pickerOpen ? 'chevronUp' : 'chevronDown'}
						borderRadiusSide="right"
						iconOnly
						useButtonProps
						noMargin
						onPress={() => dispatch(EVENT_ID.NOVA_TOGGLE_PICKER, novaInstanceKey(props.taskId, props.instanceKey))}
						aria-label={formatMessage({id: 'nova_invocation.control.choose_agent'})}
						aria-expanded={pickerOpen}
						data-cy="nova-invocation-caret"
					/>
				</SplitRow>
			);
			break;
		default: {
			const _exhaustive: never = state;
			void _exhaustive;
			control = (
				<Main emphasis="medium" disabled useButtonProps noMargin data-cy="nova-invocation-control">
					{formatMessage({id: 'nova_invocation.control.invoke'})}
				</Main>
			);
		}
	}

	const anchored = <TooltipAnchor $refused={refused}>{control}</TooltipAnchor>;

	// Withheld while this row's own agent list is open: the list covers the control it came from,
	// and a tooltip floating over an open list explains nothing.
	const tooltipped =
		tooltip && !pickerOpen ? (
			<Tooltip
				content={
					<TooltipLine $wrap={isHeader} data-cy="nova-invocation-tooltip-line">
						<Detail color="main" noWrap={!isHeader}>
							{tooltip}
						</Detail>
					</TooltipLine>
				}
				interactable={false}
			>
				{anchored}
			</Tooltip>
		) : (
			anchored
		);

	return (
		// position: relative so `agentPicker` has a defined anchor to position:absolute against,
		// regardless of whatever container each mounting surface happens to wrap this control in.
		<span
			id={controlElementId(props.taskId, props.instanceKey)}
			style={{position: 'relative', display: 'inline-flex', alignItems: 'center'}}
		>
			{tooltipped}
			<StatusRegion role="status">{statusText}</StatusRegion>
			{disabledReasonSpan}
			{state.kind === 'action' && props.agentPicker}
		</span>
	);
};
